import { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  level?: 'app' | 'page' | 'section'
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire app.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary level="page">
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * Levels:
 * - app: Top-level boundary, shows full-page error with home button
 * - page: Page-level boundary, allows refresh or navigation
 * - section: Section-level boundary, minimal disruption
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught error:', error)
      console.error('Error info:', errorInfo)
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)

    // TODO: Log to external service (Sentry, LogRocket, etc.)
    // logErrorToService(error, errorInfo)

    this.setState({
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/dashboard'
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI based on level
      const level = this.props.level || 'section'

      if (level === 'app') {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <div className="mb-4 flex justify-center">
                  <AlertTriangle className="h-12 w-12 text-destructive" />
                </div>
                <CardTitle className="text-center text-2xl">
                  Something went wrong
                </CardTitle>
                <CardDescription className="text-center">
                  The application encountered an unexpected error. Please try
                  reloading the page or return to the dashboard.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="rounded-md bg-muted p-4">
                    <p className="mb-2 text-sm font-semibold">
                      Error Details (Development Only):
                    </p>
                    <pre className="overflow-x-auto text-xs text-muted-foreground">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack && (
                        <div className="mt-2">
                          Stack:
                          {this.state.errorInfo.componentStack}
                        </div>
                      )}
                    </pre>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={this.handleReload}
                    variant="default"
                    className="flex-1"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reload Page
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="flex-1"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Go to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }

      if (level === 'page') {
        return (
          <div className="flex min-h-[400px] items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <div className="mb-2 flex justify-center">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
                <CardTitle className="text-center">Page Error</CardTitle>
                <CardDescription className="text-center">
                  This page encountered an error. Try refreshing or go back.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="rounded-md bg-muted p-3">
                    <p className="mb-1 text-xs font-semibold">Error:</p>
                    <pre className="overflow-x-auto text-xs text-muted-foreground">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={this.handleReset}
                    variant="default"
                    size="sm"
                    className="flex-1"
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Home className="mr-2 h-3 w-3" />
                    Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }

      // Section-level error (minimal)
      return (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-destructive" />
            <div className="flex-1">
              <h3 className="mb-1 font-semibold text-destructive">
                Error Loading Section
              </h3>
              <p className="mb-3 text-sm text-muted-foreground">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <Button onClick={this.handleReset} variant="outline" size="sm">
                <RefreshCw className="mr-2 h-3 w-3" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
